'use client'
import type { AnalyticsEventMap } from '~/lib/analytics/types'
import type { RedsysFormData } from '~/lib/services/api/payment'
import type { CheckoutPaymentMethod } from '~/lib/utils/payment-methods'
import { useTranslations } from 'next-intl'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from '~/i18n/routing'
import { trackEvent } from '~/lib/analytics/client'
import { getQueryClient, TAG_TYPES } from '~/lib/config'
import useCreatePayment from '~/lib/hooks/payment/use-create-payment'
import { useCartStore } from '~/lib/stores/cartStore'
import { isCardCheckoutPaymentMethod } from '~/lib/utils/payment-methods'

// RedsysFormData is now imported from the API service

interface PayButtonProps {
  paymentMethod: CheckoutPaymentMethod
  disabled?: boolean
  className?: string
  cartAnalytics?: Omit<
    AnalyticsEventMap['car_res_pagar'],
    'order_id' | 'payment_method'
  >
}

export const PayButton: React.FC<PayButtonProps> = ({
  paymentMethod,
  disabled = false,
  className = 'px-16 py-2.5 font-medium w-full text-xl bg-amber-300 rounded-xl cursor-pointer border-[none] mb-5',
  cartAnalytics,
}) => {
  const queryClient = getQueryClient()

  const t = useTranslations('common.cart')
  const router = useRouter()
  const [formData, setFormData] = useState<RedsysFormData | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // Use the payment mutation hook
  const createPaymentMutation = useCreatePayment()

  // Get shipping address from cart store
  const shippingAddress = useCartStore(state => state.address)

  // Auto-submit form when form data is ready
  useEffect(() => {
    if (formData && formRef.current) {
      formRef.current.submit()
    }
  }, [formData])

  const generateOrderId = (): string => {
    // Generate unique numeric order ID (max 12 digits for Redsys)
    // Use timestamp + random number to ensure uniqueness
    const timestamp = Date.now().toString().slice(-8) // Last 8 digits of timestamp
    const random = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, '0') // 4 random digits
    return timestamp + random // 12 digits total
  }

  const handlePayment = async () => {
    try {
      const orderId = generateOrderId()
      if (!shippingAddress) {
        console.error('No shipping address available')
        return
      }

      if (paymentMethod === 'manual') {
        // Create manual process order (quote request)
        await createPaymentMutation.mutateAsync({
          orderId,
          paymentData: {
            shipping_address: shippingAddress,
            method: 'manual',
          },
        })
        trackEvent('car_res_pagar', {
          items_count: cartAnalytics?.items_count ?? 0,
          order_id: orderId,
          payment_confirmation_amount:
            cartAnalytics?.payment_confirmation_amount,
          payment_method: 'manual',
          subtotal: cartAnalytics?.subtotal,
          total_quantity: cartAnalytics?.total_quantity ?? 0,
          total_with_vat: cartAnalytics?.total_with_vat,
        })

        // Reset cart queries
        await queryClient.resetQueries({
          queryKey: [TAG_TYPES.CART],
        })
        await queryClient.resetQueries({
          queryKey: [TAG_TYPES.ORDER, 'user-orders'],
        })

        // Navigate to confirmation (reuse transfer or specific page)
        // Using transfer instructions for now but passing manual query param
        router.push(
          `/checkout/transfer-instructions?orderId=${orderId}&type=manual`,
        )
        return
      }

      if (paymentMethod === 'transfer') {
        // Create transfer order in backend (this will close the cart)
        await createPaymentMutation.mutateAsync({
          orderId,
          paymentData: {
            shipping_address: shippingAddress,
            method: 'transfer',
          },
        })
        trackEvent('car_res_pagar', {
          items_count: cartAnalytics?.items_count ?? 0,
          order_id: orderId,
          payment_confirmation_amount:
            cartAnalytics?.payment_confirmation_amount,
          payment_method: 'transfer',
          subtotal: cartAnalytics?.subtotal,
          total_quantity: cartAnalytics?.total_quantity ?? 0,
          total_with_vat: cartAnalytics?.total_with_vat,
        })

        // Reset cart queries to reflect the closed cart
        await queryClient.resetQueries({
          queryKey: [TAG_TYPES.CART],
        })
        await queryClient.resetQueries({
          queryKey: [TAG_TYPES.ORDER, 'user-orders'],
        })

        // Navigate to transfer instructions with the order ID
        router.push(`/checkout/transfer-instructions?orderId=${orderId}`)
        return
      }

      if (isCardCheckoutPaymentMethod(paymentMethod)) {
        sessionStorage.setItem('pendingRedsysOrderId', orderId)
        const result = await createPaymentMutation.mutateAsync({
          orderId,
          paymentData: {
            shipping_address: shippingAddress,
            method: paymentMethod,
          },
        })
        trackEvent('car_res_pagar', {
          items_count: cartAnalytics?.items_count ?? 0,
          order_id: orderId,
          payment_confirmation_amount:
            cartAnalytics?.payment_confirmation_amount,
          payment_method: 'card',
          subtotal: cartAnalytics?.subtotal,
          total_quantity: cartAnalytics?.total_quantity ?? 0,
          total_with_vat: cartAnalytics?.total_with_vat,
        })
        await queryClient.resetQueries({
          queryKey: [TAG_TYPES.ORDER, 'user-orders'],
        })
        if (result.data) {
          setFormData(result.data)
        }
      }
    }
    catch (err) {
      console.error('Payment creation failed:', err)
      // Error is handled by the mutation's error state
    }
  }

  const getButtonText = () => {
    if (createPaymentMutation.isPending) {
      return t('processing', { defaultValue: 'Procesando...' })
    }

    if (paymentMethod === 'manual') {
      return t('requestQuote', {
        defaultValue: 'Solicitar pedido sin compromiso',
      })
    }

    if (isCardCheckoutPaymentMethod(paymentMethod)) {
      return t('payWithCard', { defaultValue: 'Pagar con tarjeta' })
    }

    return t('requestBankTransfer', { defaultValue: 'Solicitar transferencia' })
  }

  return (
    <>
      {paymentMethod === 'manual' && (
        <p className="mb-4 text-sm text-gray-700 font-medium">
          {t('manualQuoteDescription', {
            defaultValue:
              'El equipo de todobarro se pondrá en contacto con usted cuando revise el pedido, por favor clique en el siguiente botón para confirmar su pedido sin compromiso.',
          })}
        </p>
      )}
      <button
        className={`${className} ${disabled || createPaymentMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={handlePayment}
        disabled={disabled || createPaymentMutation.isPending}
      >
        {getButtonText()}
      </button>

      {createPaymentMutation.error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {createPaymentMutation.error.message
            || t('paymentError', { defaultValue: 'Error en el pago' })}
        </div>
      )}

      {/* Hidden form for Redsys redirect */}
      {formData && (
        <form
          ref={formRef}
          action={formData.url}
          method="POST"
          className="hidden"
        >
          <input
            type="hidden"
            name="Ds_SignatureVersion"
            value={formData.Ds_SignatureVersion}
            readOnly
          />
          <input
            type="hidden"
            name="Ds_MerchantParameters"
            value={formData.Ds_MerchantParameters}
            readOnly
          />
          <input
            type="hidden"
            name="Ds_Signature"
            value={formData.Ds_Signature}
            readOnly
          />
        </form>
      )}
    </>
  )
}
